import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import { action, autorun, computed, makeObservable, observable, reaction } from 'mobx';

import { getRequest, postRequest } from '../../services/api';
import { ldClient } from '../../services/launchDarkly';
import type { ApiResponse } from '../../services/types';
import { getErrorMessage } from '../../utils/errors';
import { devError, devWarn } from '../../utils/log';
import type { RootStore } from '../RootStore';

import { formatJackpotAmount, formatJackpotAmountRounded, isRoundedJackpotAmount } from './formatJackpotAmount';
import { trackJackpotWidgetOptIn, trackJackpotWidgetShown } from './jackpotAnalytics';
import { publishJackpotBadgeAttributes } from './jackpotBadgeAttributes';
import {
  JACKPOT_FLAG,
  resolveJackpotFlag,
  resolveJackpotLobbyPoll,
  resolveJackpotMaintenance,
} from './resolveJackpotFlag';
import {
  toJackpotCoin,
  type JackpotCoinType,
  type JackpotEligibility,
  type JackpotLobbyState,
  type JackpotWidgetDecision,
} from './types';

// GET /api/v1/jackpot/eligibility. The version segment is NOT written here: the axios request
// interceptor prefixes `/v1/` onto every non-`/v2` url, so spelling it out yields /v1/v1/....
const ELIGIBILITY_PATH = 'jackpot/eligibility';

/** Both coins a jackpot can run on. Two and only two — the lobby toggle has no third position. */
const JACKPOT_COINS = ['CC', 'SC'] as const satisfies readonly JackpotCoinType[];

type ByCoin<T> = Record<JackpotCoinType, T>;

const byCoin = <T>(value: T): ByCoin<T> => ({ CC: value, SC: value });

/**
 * Enrolled games for the current coin — what puts the Crown Jackpot badge on a lobby tile.
 *
 * Fetched alongside eligibility rather than per tile: a lobby renders dozens of cards and each one
 * asking "am I in the jackpot?" would be dozens of requests for one answer that is the same for all
 * of them.
 */
/**
 * The widget's pacing decision — whether to render it at all, and whether it may open the popup by
 * itself. Fetched alongside eligibility because it is per coin and per player exactly like the rest,
 * and because the two answers are read by the same render pass.
 */
const WIDGET_PATH = 'jackpot/widget';

/**
 * GET /api/v1/jackpot/lobby — BOTH coins' eligibility and widget decision in one response.
 *
 * Replaces four requests a tick ({@link ELIGIBILITY_PATH} and {@link WIDGET_PATH}, once per coin).
 * Behind {@link JACKPOT_LOBBY_POLL_FLAG} for one release; the per-coin paths above stay until it is at 100%,
 * and core keeps serving them indefinitely for the clients on their own release trains.
 */
const LOBBY_PATH = 'jackpot/lobby';

/**
 * How long an opt-in will wait on the lobby refresh that follows it.
 *
 * The CTA spins until this resolves and the client sets no request timeout anywhere else, so without
 * a bound a stalled refresh leaves a SUCCESSFUL opt-in spinning for good. Only the opt-in path needs
 * it — the poll genuinely does not care how long a tick takes.
 */
const OPT_IN_REFRESH_TIMEOUT_MS = 10_000;

/** A refresh on returning to the tab or the lobby is skipped if the data is younger than this. */
const REFRESH_IF_STALE_MS = 30_000;

const PARTICIPATION_PATH = 'jackpot/participation';

/** The surface an opt-in came from, recorded on core's opt-in history (a free `^[A-Z_]+$` identifier). */
export type JackpotOptInSource = 'LOBBY_WIDGET_CTA' | 'WINNERS_BANNER_POPUP';

const WINS_PATH = 'jackpot/wins';

/**
 * How close together two pending-win checks may be.
 *
 * Was 2s, which only ever collapsed a SIMULTANEOUS burst. The calls it needs to collapse are not
 * simultaneous: the poll reaction re-fires as the LD flag, auth and the coin each resolve, and those
 * land seconds apart — measured at +2.7s and +3.3s on a devspace lobby load, so every one of them
 * cleared a 2s floor and a single load cost four `wins?status=PENDING` round trips.
 *
 * Half the poll interval: long enough that a load burst is one request, short enough that it can never
 * delay a tick (the tick is 60s away, always past the floor). The one caller that must not be floored
 * — a player walking out of a game they just won in — passes `immediate`.
 */
const PENDING_WIN_MIN_INTERVAL_MS = 30_000;

/**
 * The floor an `immediate` check still respects.
 *
 * Removing the floor entirely for the leaving-a-game path was wrong: React remounts that effect
 * (StrictMode in development, and any parent re-render in production), and the two calls are a few
 * milliseconds apart rather than simultaneous — so the in-flight share misses them and both reached
 * the server. Two seconds is the old global floor: nobody wins twice inside it, and it is far below
 * any latency a player would notice walking out of a game.
 */
const PENDING_WIN_IMMEDIATE_MIN_INTERVAL_MS = 2000;

/**
 * Progressive pots tick up continuously, so the lobby total is a live value rather than a snapshot.
 * One shared poll backs every jackpot surface (category row, banner, crown badges) — nothing fetches
 * per component or per tile.
 *
 * The count-up between ticks is what makes the number read as "moving", not the tick itself: the
 * sweep re-derives its step count from whichever interval is in force, so doubling the interval
 * halves the per-step delta rather than leaving the figure static. Server-side the pot projection is
 * only refreshed every 5s anyway, so neither cadence is reading a truly live number.
 */
const REFRESH_INTERVAL_MS = 30_000;

/** The merged poll's cadence — see {@link LOBBY_POLL_FLAG}. */
const LOBBY_REFRESH_INTERVAL_MS = 60_000;

/**
 * The contract documents the bare envelope, while player-backend wraps responses in
 * `{ data: { success, data } }`. Accept either, so the row works whichever way the proxy lands, and
 * reject anything that is not recognisably the eligibility envelope (`canSee` is the one field that
 * is always present, including on the `canSee: false` response).
 */
export function parseEligibility(response: unknown): JackpotEligibility | null {
  const body = (response as { data?: unknown })?.data;
  const candidate = ((body as { data?: unknown })?.data ?? body ?? response) as Partial<JackpotEligibility>;

  if (!candidate || typeof candidate.canSee !== 'boolean') {
    return null;
  }

  const rawCategoryIds = (candidate as { eligibleCategoryIds?: unknown }).eligibleCategoryIds;

  return {
    ...candidate,
    levels: Array.isArray(candidate.levels) ? candidate.levels : [],
    // Absent stays absent — `[]` here would read as "no row is configured" and hide the collection on
    // every environment whose core predates the field.
    ...(Array.isArray(rawCategoryIds)
      ? {
          eligibleCategoryIds: rawCategoryIds
            // `null`/`''` must be dropped BEFORE Number(), which turns both into 0 — a real category
            // id that would badge whichever row happens to carry it.
            .filter((id) => typeof id === 'number' || (typeof id === 'string' && id.trim() !== ''))
            .map(Number)
            .filter((id) => Number.isFinite(id)),
        }
      : {}),
  } as JackpotEligibility;
}

/**
 * Same either-envelope tolerance as {@link parseEligibility}.
 *
 * `show` and `jump` must both be real booleans: they are the only two fields acted on, and coercing a
 * missing one would turn "core said nothing" into a decision. Null instead means NO decision, which
 * reads as "render the widget, do not auto-open" — see {@link JackpotStore.widgetAllowsDisplay}.
 */
export function parseWidgetDecision(response: unknown): JackpotWidgetDecision | null {
  const body = (response as { data?: unknown })?.data;
  const candidate = ((body as { data?: unknown })?.data ?? body ?? response) as Partial<JackpotWidgetDecision>;

  if (typeof candidate?.show !== 'boolean' || typeof candidate?.jump !== 'boolean') {
    return null;
  }

  return candidate as JackpotWidgetDecision;
}

/**
 * The merged lobby payload, split back into the two per-coin halves the store already holds.
 *
 * Each half goes through {@link parseEligibility} / {@link parseWidgetDecision} unchanged, so there is
 * exactly one place that decides what a valid envelope looks like. A coin core did not answer for is
 * `null` on both halves, which is how every other failure already reads.
 *
 * `eligibleCategoryIds` is GRAFTED back onto each half, and only when core actually sent it: the
 * store's selectors read it off the envelope, and `undefined` (core predates the field) must stay
 * distinguishable from `[]` (core says no row is configured) — three tests turn on exactly that.
 */
export function parseLobbyState(
  response: unknown,
): ByCoin<{ eligibility: JackpotEligibility | null; widget: JackpotWidgetDecision | null }> | null {
  const body = (response as { data?: unknown })?.data;
  const candidate = ((body as { data?: unknown })?.data ?? body ?? response) as Partial<JackpotLobbyState>;
  const coins = candidate?.coins;

  if (!coins || typeof coins !== 'object') {
    return null;
  }

  const hasCategoryIds = Array.isArray(candidate.eligibleCategoryIds);

  return JACKPOT_COINS.reduce(
    (acc, coinType) => {
      const half = coins[coinType];

      acc[coinType] = {
        eligibility: half
          ? parseEligibility(hasCategoryIds ? { ...half, eligibleCategoryIds: candidate.eligibleCategoryIds } : half)
          : null,
        widget: half?.widget ? parseWidgetDecision(half.widget) : null,
      };

      return acc;
    },
    {} as ByCoin<{ eligibility: JackpotEligibility | null; widget: JackpotWidgetDecision | null }>,
  );
}

/**
 * Win ids out of `GET /jackpot/wins`, through the same one-or-two `data` envelopes the other jackpot
 * reads come wrapped in. Anything that is not a list of objects with a string `id` is no wins — this
 * decides whether a popup is thrown in the player's face, so a shape it does not recognise must mean
 * "nothing" rather than "something".
 */
export function parsePendingWinIds(response: unknown): string[] {
  const body = (response as { data?: unknown })?.data;
  const candidate = (body as { data?: unknown })?.data ?? body ?? response;

  if (!Array.isArray(candidate)) {
    return [];
  }

  return candidate
    .map((win) => (win as { id?: unknown })?.id)
    .filter((id): id is string => typeof id === 'string' && id.length > 0);
}

export class JackpotStore {
  private readonly rootStore: RootStore;

  private refreshTimer: ReturnType<typeof setInterval> | null = null;

  // Monotonic request id: a coin switch restarts the poll, and the in-flight request for the
  // previous coin must not overwrite the newer one when it lands late.
  /**
   * Per coin, not global. A single counter was enough while a switch discarded everything; now that a
   * response is filed under the coin it was asked for, only a NEWER request for the SAME coin may
   * supersede it — a global counter would let SC's response silence CC's.
   */
  private requestId: ByCoin<number> = byCoin(0);

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);

    /*
     * The badge gate, published to `<body>` for CSS to act on. Same pattern as UiStore's `data-dialog`.
     *
     * WHY NOT A PER-TILE READ. A lobby renders dozens of cards, and a badge that reads the store is an
     * observer per card: every coin switch and every widget-decision change re-rendered all of them,
     * to change nothing but visibility. Two attributes here mean the whole lobby, the search results
     * and every carousel react in one style recalculation, with no React work at all.
     *
     * `data-jp-coin` is the coin whose jackpot is live — a tile shows its badge only for a group on
     * that coin, since a game is usually enrolled in both. `data-jp-badges` is the global gate
     * (maintenance, widget hidden, decision not settled yet).
     */
    autorun(() => {
      publishJackpotBadgeAttributes(toJackpotCoin(this.rootStore.coinTypeStore.coinType), this.canShowGameBadge);
    });

    this.readFlag();
    ldClient?.on('ready', () => this.readFlag());
    ldClient?.on('change', (changes) => {
      if (JACKPOT_FLAG in changes) {
        this.readFlag();
      }
    });

    /*
     * NOT keyed on the coin any more. Both coins are polled together and held together, so a switch
     * needs no refetch and must not restart the poll — that restart, and the `setEligibility(null)`
     * that came with it, WAS the delay: every toggle blanked the widget for a round trip.
     *
     * Auth and the flag still gate it, and still clear everything: those really do invalidate both
     * coins.
     */
    reaction(
      () => ({
        enabled: this.isEnabled,
        authorized: this.rootStore.authStore.authorized,
        // In the key so flipping the rollout flag tears the poll down and rebuilds it at the other
        // endpoint and the other cadence, rather than leaving a running timer on the old one.
        lobbyPoll: this.lobbyPollEnabled,
      }),
      ({ enabled, authorized }) => {
        this.stopPolling();
        this.stopAmountTween();
        this.setTweenedAmount(null);
        this.clearEligibility();
        this.resetWidgetDecision();

        if (enabled && authorized) {
          /*
           * The FIRST tick goes through the same gate as every later one.
           *
           * It used to be an ungated pair of calls, which meant two things: deep-linking straight into
           * a game fetched lobby data for a lobby that was not on screen, and any re-fire of this
           * reaction (an LD re-init, a dev-server hot reload) polled from inside a game however long
           * the player had been there. Coming back out is covered — `GameIframe` calls
           * `refreshIfStale` on the way to the lobby.
           */
          this.pollTick();
          /*
           * Both on the tick.
           *
           * A win the player left unclaimed is offered again when they come back to the lobby
           * (`GameIframe` fires it on the way out of the game) — but a win can also land for a player
           * who is already sitting here: another device, another tab, or a `JP_WIN` the game document
           * never delivered. The poll is what finds those, and its own guards are what keep it from
           * being an interruption: nothing opens while the player is in a game or already has a dialog
           * up, and a win is only ever offered once.
           */
          /*
           * Gated INSIDE the callback rather than by clearing the timer: the timer costs nothing, it
           * resumes on its own, and tearing it down on every alt-tab would re-anchor the count-up each
           * time. Same choice `SlotBattleStore.schedulePoll` made, for the same reason.
           */
          this.refreshTimer = setInterval(() => this.pollTick(), this.pollIntervalMs);
        }
      },
      {
        // The data function builds a fresh object every run, so without a value comparator mobx's
        // default reference equality would re-fire (and restart the poll) on every unrelated tick.
        equals: isEqual,
        // The flag is resolved above, before this reaction exists, and a returning player can already
        // be authorized by the time the store is constructed — without this the first poll never starts.
        fireImmediately: true,
      },
    );

    this.watchCoinSwitch();
    this.watchTabVisibility();
  }

  /**
   * Refresh on returning to the tab.
   *
   * The gate above skips ticks while hidden, so without this the first paint after an alt-tab shows a
   * pot up to a full interval old. Same pattern, same guards as `RewardsStore`'s own
   * `visibilitychange` listener; the staleness floor in {@link refreshIfStale} is what stops a player
   * flicking between tabs turning the gate into MORE requests than it saved.
   */
  private watchTabVisibility() {
    if (typeof document === 'undefined') {
      return;
    }

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') {
        return;
      }

      void this.refreshIfStale();
    });
  }

  /**
   * One poll tick, gate and all — the reaction's first call and every interval firing go through here
   * so neither can be gated differently from the other.
   *
   * Before this the poll ran on every route and in every background tab. In a game it was refreshing a
   * lobby surface nobody can see, while game-ui polls `/overview` itself for the banner that IS
   * visible — those ticks were pure duplication.
   */
  private pollTick() {
    if (this.lobbyPollEnabled && !this.shouldPollNow) {
      return;
    }

    void this.fetchEligibility();
    void this.maybeOpenPendingWin();
  }

  /** 60s on the merged poll, 30s on the legacy per-coin one. */
  private get pollIntervalMs(): number {
    return this.lobbyPollEnabled ? LOBBY_REFRESH_INTERVAL_MS : REFRESH_INTERVAL_MS;
  }

  /**
   * Whether a tick is worth making: the tab is visible, and the player is in the lobby rather than in
   * a game.
   *
   * `pathname.startsWith('/games')` read at tick time rather than through a router observable is
   * already what "in a game" means in this store and in four other places in this codebase.
   */
  private get shouldPollNow(): boolean {
    if (typeof document !== 'undefined' && document.hidden) {
      return false;
    }

    return !window.location.pathname.startsWith('/games');
  }

  /**
   * One refresh, if the data is old enough to be worth one.
   *
   * Called on returning to the tab and on leaving a game — the two moments the gate above has just
   * stopped polling through. Every guard matters: the two the poll itself has, the two the gate has,
   * and the floor that collapses a burst of visibility flips into a single request.
   */
  public async refreshIfStale(minIntervalMs = REFRESH_IF_STALE_MS): Promise<void> {
    if (!this.isEnabled || !this.rootStore.authStore.authorized || !this.lobbyPollEnabled) {
      return;
    }

    if (!this.shouldPollNow || Date.now() - this.lastFetchAt < minIntervalMs) {
      return;
    }

    await this.fetchEligibility();
  }

  /**
   * Re-anchor the count-up on a coin switch.
   *
   * The tween is one number for whichever coin is showing. Left alone, switching would animate the new
   * coin's pot up from the old coin's value — a CC pot sweeping down from an SC one, or a visible
   * jump. Nothing is refetched here; the data for both coins is already in hand.
   */
  private watchCoinSwitch() {
    reaction(
      () => toJackpotCoin(this.rootStore.coinTypeStore.coinType),
      () => {
        this.stopAmountTween();
        this.setTweenedAmount(null);
        this.sweptTo = null;
        this.startAmountSweep();
        this.maybeAutoOpenPopup();
      },
    );
  }

  /**
   * Both coins back to "never asked" — sign-out and the kill switch, never a switch.
   *
   * The request ids go FIRST, and that ordering is the whole point. Emptying the maps does nothing to
   * a request already on the wire: it resolves a moment later, still matching its id, and writes the
   * state straight back. No display getter checks `authorized`, so after a sign-out that is the
   * PREVIOUS player's jackpot reappearing in the lobby. Bumping the ids makes every in-flight response
   * stale, so it lands on a guard instead of on the store.
   */
  @action
  private clearEligibility() {
    this.requestId = { CC: this.requestId.CC + 1, SC: this.requestId.SC + 1 };
    this.eligibilityByCoin = byCoin(null);
    this.eligibilityAskedByCoin = byCoin(false);
  }

  /**
   * Both halves of the merged response, in one action.
   *
   * Written through the existing per-coin setters rather than straight into the maps: `setEligibility`
   * carries the "only the coin on screen animates" rule and `setWidgetDecision` carries the "file
   * under the response's own coin, and settle it even when null" rule. Both are load-bearing and both
   * have tests.
   *
   * `null` is the degraded case and reads exactly as the per-coin failure paths already do: no
   * eligibility (which renders as `canSee: false`) and a settled-but-null decision (which renders the
   * widget unpaced rather than removing it).
   *
   * {@link maybeAutoOpenPopup} is called ONCE, after both coins are in. With one response there is no
   * longer a window in which the inactive coin's stale `jump: true` can act while the selected coin's
   * own answer is still in flight — that race is gone by construction, not by a guard.
   */
  @action
  private applyLobbyState(state: ReturnType<typeof parseLobbyState>) {
    JACKPOT_COINS.forEach((coinType) => {
      this.setEligibility(state ? state[coinType].eligibility : null, coinType);
      this.setWidgetDecision(state ? state[coinType].widget : null, coinType);
    });

    this.maybeAutoOpenPopup();
  }

  /** One LD read, two derived states — they must never disagree about which branch the lobby is in. */
  private readFlag() {
    this.setEnabled(resolveJackpotFlag(JACKPOT_FLAG));
    this.setInMaintenance(resolveJackpotMaintenance(JACKPOT_FLAG));
    // Defaults OFF, and carries a dev-only override so the new path can be looked at before the flag
    // exists in LD — see resolveJackpotLobbyPoll.
    this.setLobbyPollEnabled(resolveJackpotLobbyPoll());
  }

  /**
   * The rollout switch for the merged poll — endpoint, cadence and gating as one bundle.
   *
   * ONE flag, not three. The cadence and the gate are behaviour changes that cannot be undone without
   * a release, and the merged endpoint's first contact with production traffic is the lobby of every
   * player; a single flag gives seconds-to-rollback and a 1% -> 10% -> 100% ramp. Three flags would be
   * three code paths and seven combinations, one of which would ship broken.
   */
  @observable
  public lobbyPollEnabled = false;

  @action
  public setLobbyPollEnabled(enabled: boolean) {
    this.lobbyPollEnabled = enabled;
  }

  /** When the last poll response landed — the staleness floor in {@link refreshIfStale} reads it. */
  private lastFetchAt = 0;

  @observable
  public isEnabled = false;

  @action
  public setEnabled(enabled: boolean) {
    this.isEnabled = enabled;
  }

  /**
   * The flag is in a maintenance window: no request is made and nothing is fetched, but the lobby still
   * renders the small widget's maintenance plate. See {@link canShowMaintenanceWidget}.
   */
  @observable
  public isInMaintenance = false;

  @action
  public setInMaintenance(maintenance: boolean) {
    this.isInMaintenance = maintenance;
  }

  /**
   * BOTH COINS, held at once.
   *
   * This used to be one payload plus the coin it belonged to, and a coin switch threw it away and
   * refetched — so flipping GC/SC blanked the widget for a round trip every time. The lobby has two
   * coins and only two, the payload is small, and the player toggles between them constantly; keeping
   * both means a switch is a re-read of memory, with no request in the path at all.
   *
   * Written per coin so a response can never land under the wrong icon: the slot is chosen by the coin
   * WE asked for, not by the one the lobby happens to show when it arrives.
   */
  @observable
  public eligibilityByCoin: ByCoin<JackpotEligibility | null> = byCoin(null);

  /** Whether a fetch for that coin has completed — distinct from it having returned anything. */
  @observable
  public eligibilityAskedByCoin: ByCoin<boolean> = byCoin(false);

  @observable
  public widgetDecisionByCoin: ByCoin<JackpotWidgetDecision | null> = byCoin(null);

  /**
   * Whether the decision request for that coin has come back — either with a decision or with a
   * failure. Distinct from the decision being non-null, which cannot tell "not asked yet" from "asked
   * and got nothing", and those two must render differently: see {@link widgetAllowsDisplay}.
   */
  @observable
  public widgetSettledByCoin: ByCoin<boolean> = byCoin(false);

  @action
  public setWidgetDecision(decision: JackpotWidgetDecision | null, coinType: JackpotCoinType | null) {
    if (!coinType) {
      return;
    }

    this.widgetDecisionByCoin = { ...this.widgetDecisionByCoin, [coinType]: decision };
    this.widgetSettledByCoin = { ...this.widgetSettledByCoin, [coinType]: true };
  }

  /** Back to "not asked yet" on BOTH coins — for sign-out and the flag going off, not for a switch. */
  @action
  public resetWidgetDecision() {
    this.widgetDecisionByCoin = byCoin(null);
    this.widgetSettledByCoin = byCoin(false);
  }

  /**
   * Auto-open happens AT MOST ONCE per store lifetime — one lobby session.
   *
   * Core's cooldown is the real pacing, but it is measured from `last_jump_at`, which only moves once
   * the popup reports its impression. Between the jump and that write, a poll tick would still see
   * `jump: true`. This flag is what makes that race unable to reopen a popup the player just closed.
   *
   * Deliberately NOT reset on a coin switch: toggling GC/SC is not a new session, and a player
   * flipping coins should not be jumped into the popup twice.
   */
  private hasAutoOpenedPopup = false;

  /** The coin on screen, once a fetch for it has completed. Kept for callers that tag events by group. */
  @computed
  public get loadedCoinType(): JackpotCoinType | null {
    const coinType = toJackpotCoin(this.rootStore.coinTypeStore.coinType);

    return this.eligibilityAskedByCoin[coinType] ? coinType : null;
  }

  @action
  public setEligibility(eligibility: JackpotEligibility | null, coinType: JackpotCoinType | null) {
    if (!coinType) {
      return;
    }

    this.eligibilityByCoin = { ...this.eligibilityByCoin, [coinType]: eligibility };
    this.eligibilityAskedByCoin = { ...this.eligibilityAskedByCoin, [coinType]: true };

    // Only the coin on screen animates. The other one's arrival must not restart the visible tween,
    // or the number the player is watching jumps every time the background fetch lands.
    if (coinType === toJackpotCoin(this.rootStore.coinTypeStore.coinType)) {
      this.startAmountSweep();
    }
  }

  /** The payload for the coin the lobby currently shows — a plain lookup now that both are held. */
  @computed
  public get eligibilityForSelectedCoin(): JackpotEligibility | null {
    if (!this.isEnabled) {
      return null;
    }

    return this.eligibilityByCoin[toJackpotCoin(this.rootStore.coinTypeStore.coinType)];
  }

  /**
   * The single gate for every jackpot lobby surface. False on `canSee: false`, on any error, and
   * while the first fetch is in flight — callers render NOTHING in that case, no skeleton and no
   * error state, so a jackpot outage is invisible in the lobby.
   */
  @computed
  public get canSeeJackpot(): boolean {
    return this.eligibilityForSelectedCoin?.canSee === true;
  }

  /**
   * The TOP LEVEL's pot — the figure every jackpot surface prints: the category row, both widgets and
   * the badge on a game tile.
   *
   * It used to be the sum across all levels. A sum is not a prize anyone can win, and the number under
   * the crown is an advert for the biggest one on the ladder. Which level is "top" is the operator's
   * own arrangement (level association priority), resolved by core.
   */
  @computed
  public get topLevelAmount(): number | null {
    if (!this.canSeeJackpot) {
      return null;
    }

    // `totalAmount` is core's deprecated alias, read as a fallback so this works against a core that
    // predates the rename. Both carry the same value once it ships.
    const eligibility = this.eligibilityForSelectedCoin;
    const raw = eligibility?.topLevelAmount ?? eligibility?.totalAmount;

    // `Number(null)` is 0 and `Number('')` is 0 — both finite — so a nil total used to surface as a
    // pot of zero. Core sends null whenever Splash was unreachable (`amountsLive: false`), and that
    // is "unknown", not "empty": every jackpot surface must render no figure rather than a 0.
    if (isNil(raw) || raw === '') {
      return null;
    }

    const total = Number(raw);

    return Number.isFinite(total) ? total : null;
  }

  /**
   * The decision, but only if it belongs to the coin the lobby currently shows — same guard as
   * {@link eligibilityForSelectedCoin}, for the same reason.
   */
  @computed
  public get widgetDecisionForSelectedCoin(): JackpotWidgetDecision | null {
    return this.widgetDecisionByCoin[toJackpotCoin(this.rootStore.coinTypeStore.coinType)];
  }

  /** Has the decision for the coin on screen settled? Per coin, so a switch never re-enters "asking". */
  @computed
  private get widgetSettledForSelectedCoin(): boolean {
    return this.widgetSettledByCoin[toJackpotCoin(this.rootStore.coinTypeStore.coinType)];
  }

  /**
   * What core's decision says about rendering — and the difference between "not asked yet" and
   * "asked, got nothing".
   *
   * BEFORE the request settles: hidden. Rendering optimistically and correcting on arrival is what
   * made the widget appear for a beat and vanish; the request is concurrent with the eligibility one
   * that produces `totalAmount`, so the wait costs no extra round trip.
   *
   * AFTER it settles with no decision: shown. That is an outage, or a core older than the endpoint,
   * and the widget existing is the behaviour that shipped — neither should silently remove it.
   *
   * The auto-open is the opposite in both cases: it needs an explicit yes (see
   * {@link maybeAutoOpenPopup}), because a popup nobody asked for is the failure players notice.
   */
  @computed
  private get widgetAllowsDisplay(): boolean {
    if (!this.widgetSettledForSelectedCoin) {
      return false;
    }

    return this.widgetDecisionForSelectedCoin?.show ?? true;
  }

  /**
   * The pot AS PRINTED — the one string every lobby surface renders.
   *
   * Six of them show this figure: both widgets, the promo card, the nav widget, the category row
   * header and the badge on a game tile. Each used to call `toLocaleString()` itself, which is not one
   * rule but six copies of an almost-rule: it prints up to THREE fraction digits and drops trailing
   * zeros, so a live pot ticking through .5 then .567 rendered differently on each surface and changed
   * width as it moved — and none of them agreed with the popup, which formats through game-ui's own
   * helper.
   *
   * Formatting here rather than in each component is what makes that impossible to get wrong again: a
   * new surface renders one of these and inherits its rule. `topLevelAmount` stays a number for the
   * code that needs one — the display gate and the analytics payload, where a formatted string would
   * be wrong.
   * Empty string when there is no figure, which is the same "render nothing" every surface already
   * handles: `null` is Splash being unreachable, not a pot of zero.
   */
  @observable
  private tweenedAmount: number | null = null;

  private tweenTimer: ReturnType<typeof setInterval> | null = null;

  /**
   * The pot the last sweep was aimed at, kept after it finishes.
   *
   * `tweenedAmount` goes back to `null` on completion so the labels read the real figure, which then
   * leaves nothing to say where the previous sweep ENDED — and without that the next one cannot tell
   * "unchanged pot, do not move" from "first figure of the session, count up from the floor".
   */
  private sweptTo: number | null = null;

  private static readonly TWEEN_INTERVAL_MS = 60;

  private static easeOutCubic(t: number): number {
    return 1 - Math.pow(1 - t, 3);
  }

  private static readonly MIN_TRAVEL_UNITS = 20;
  private static readonly MIN_TRAVEL_FRACTION = 0.01;
  private static readonly MAX_TRAVEL_FRACTION = 0.5;

  private static amountSweepStart(target: number): number {
    const travel = Math.min(
      Math.max(JackpotStore.MIN_TRAVEL_UNITS, target * JackpotStore.MIN_TRAVEL_FRACTION),
      target * JackpotStore.MAX_TRAVEL_FRACTION,
    );

    return target - travel;
  }

  @action
  private startAmountSweep(): void {
    const target = this.topLevelAmount;

    if (isNil(target) || target <= 0) {
      this.stopAmountTween();
      this.tweenedAmount = null;
      this.sweptTo = null;

      return;
    }

    const from = this.tweenedAmount ?? this.sweptTo ?? JackpotStore.amountSweepStart(target);

    if (from >= target) {
      this.stopAmountTween();
      this.tweenedAmount = null;
      this.sweptTo = target;

      return;
    }

    this.stopAmountTween();
    this.sweptTo = target;

    const steps = REFRESH_INTERVAL_MS / JackpotStore.TWEEN_INTERVAL_MS;
    let step = 0;

    this.tweenedAmount = from;
    this.tweenTimer = setInterval(() => {
      step += 1;

      if (step >= steps) {
        this.setTweenedAmount(null);
        this.stopAmountTween();

        return;
      }

      this.setTweenedAmount(from + JackpotStore.easeOutCubic(step / steps) * (target - from));
    }, JackpotStore.TWEEN_INTERVAL_MS);
  }

  @action
  private setTweenedAmount(value: number | null): void {
    this.tweenedAmount = value;
  }

  @action
  private stopAmountTween(): void {
    if (this.tweenTimer) {
      clearInterval(this.tweenTimer);
      this.tweenTimer = null;
    }
  }

  /** The figure to render: the tween while one is running, the real pot otherwise. */
  @computed
  private get displayAmount(): number | null {
    return this.tweenedAmount ?? this.topLevelAmount;
  }

  /**
   * The value a given surface should FORMAT.
   *
   * A surface that abbreviates to K/M takes the settled figure, never the tween: see
   * {@link isRoundedJackpotAmount}. So the category row (which never abbreviates) counts, and the
   * widgets count until the pot outgrows their digit budget and then snap.
   */
  private amountForBudget(fullUpToDigits: number): number | null {
    return isRoundedJackpotAmount(this.topLevelAmount, { fullUpToDigits }) ? this.topLevelAmount : this.displayAmount;
  }

  @computed
  public get topLevelAmountLabel(): string {
    return formatJackpotAmount(this.displayAmount);
  }

  @computed
  public get topLevelAmountLabelCompact(): string {
    return formatJackpotAmountRounded(this.amountForBudget(7), { fullUpToDigits: 7, kUpToDigits: 7 });
  }

  @computed
  public get topLevelAmountLabelBadge(): string {
    return formatJackpotAmountRounded(this.amountForBudget(10), { fullUpToDigits: 10, kUpToDigits: 10 });
  }

  @computed
  public get topLevelAmountLabelWide(): string {
    return formatJackpotAmountRounded(this.amountForBudget(8), { fullUpToDigits: 8, kUpToDigits: 8 });
  }

  /**
   * Single gate for EVERY Crown Jackpot lobby surface — promo card, nav icon, the category row and its
   * header, and the badge on a game thumbnail. Three things have to hold: the jackpot is visible to
   * this player, its total is a usable number, and core's pacing rules allow it on screen right now.
   *
   * One gate on purpose. The row and the tile badges used to be gated on the amount alone, so a
   * display window that hid the widget still left a Crown Jackpot collection and badged thumbnails
   * advertising a jackpot with no way into it. Whatever `jackpot_widget_config` decides, the whole
   * feature appears and disappears together.
   */
  @computed
  public get canShowWidget(): boolean {
    return this.topLevelAmount !== null && this.widgetAllowsDisplay;
  }

  /**
   * The maintenance plate, which is the ONE thing a maintenance window renders.
   *
   * Deliberately independent of everything the live widget needs: in maintenance the flag is not on, so
   * the store never polls, there is no eligibility payload, no amount and no widget decision. Gating
   * this on any of them would render nothing, which is the state this exists to replace.
   */
  @computed
  public get canShowMaintenanceWidget(): boolean {
    return this.isInMaintenance;
  }

  /**
   * Gate for the lobby jackpot collection — the ROW, not just its header.
   *
   * Deliberately the SAME value as {@link canShowWidget}, kept as its own name because the row is its
   * own surface and callers read better for it. A row advertising a jackpot the player has no widget
   * to open is the state this now prevents; the header's own render guard reads this too, or the row
   * appears with an empty amount.
   */
  @computed
  public get canShowCategoryRow(): boolean {
    return this.canShowWidget;
  }

  /**
   * Gate for the Crown Jackpot badge on a lobby game thumbnail.
   *
   * Same value again: a badged tile is an advert for the jackpot, so it follows the widget rather than
   * outliving it. WHICH games carry a badge is not asked here — it arrives per game on the games
   * listing (`Game.jackpots`), because a group can enrol ~50k brand games and no id set that size
   * belongs on a lobby load.
   *
   * Published to `<body>` rather than read per tile — see the autorun in the constructor.
   */
  @computed
  public get canShowGameBadge(): boolean {
    return this.canShowWidget;
  }

  /**
   * Lobby rows configured as jackpot collections (KRA-303) — the CATEGORY + INCLUDE ids from
   * `splash_jackpot.jackpot_eligible_slot_rule`, as core sent them.
   *
   * `undefined` distinguishes "core did not send the field" (older build; `isJackpotCategory` then
   * falls back to the row name) from `[]`, which is core stating that NO row is configured.
   */
  @computed
  public get eligibleCategoryIds(): number[] | undefined {
    return this.eligibilityForSelectedCoin?.eligibleCategoryIds;
  }

  /**
   * Coins whose `lobby_widget` impression has already been reported. Not observable — nothing renders
   * from it.
   */
  private readonly widgetImpressionReported = new Set<JackpotCoinType>();

  /**
   * Report that the lobby widget was drawn, at most once per coin.
   *
   * Deduped HERE rather than per component because three surfaces can be on the lobby at the same
   * time (promo card, rail widget, nav icon) and they are one exposure to the player, not three. A
   * coin switch is a new exposure: the pool and the join state are per coin.
   *
   * The pool figure is whatever is loaded at this moment — omitted while the overview is still in
   * flight. Waiting for it would mean losing the impression entirely whenever Splash is unreachable,
   * which is exactly when knowing the widget was still shown matters.
   */
  public reportWidgetShown(): void {
    const coinType = toJackpotCoin(this.rootStore.coinTypeStore.coinType);

    if (this.widgetImpressionReported.has(coinType)) {
      return;
    }

    this.widgetImpressionReported.add(coinType);
    trackJackpotWidgetShown(coinType, this.isOptedIn, this.topLevelAmount);
  }

  /** Current opt-in state for the coin the lobby is showing — drives OPT IN vs the PLAY NOW CTA. */
  @computed
  public get isOptedIn(): boolean {
    return this.isOptedInFor(toJackpotCoin(this.rootStore.coinTypeStore.coinType));
  }

  /**
   * One coin's payload, whichever coin the lobby shows. The winners banner's Crown Jackpot pop-up asks
   * about the WIN's coin, which is not necessarily the lobby's.
   */
  private eligibilityFor(coinType: JackpotCoinType): JackpotEligibility | null {
    return this.isEnabled ? this.eligibilityByCoin[coinType] : null;
  }

  public isOptedInFor(coinType: JackpotCoinType): boolean {
    return this.eligibilityFor(coinType)?.participate === true;
  }

  /** Barred from joining (segment/jurisdiction) — offered PLAY rather than an OPT IN the server refuses. */
  public isBarredFrom(coinType: JackpotCoinType): boolean {
    return this.eligibilityFor(coinType)?.canParticipate === false;
  }

  /** OPT IN would be accepted: eligible and not in yet. False while the jackpot is down or unloaded. */
  public canOptInFor(coinType: JackpotCoinType): boolean {
    const eligibility = this.eligibilityFor(coinType);

    return eligibility?.canParticipate === true && eligibility.participate === false;
  }

  /**
   * CTA label gate for the promo card (FE_DESIGN_SPEC §7.4 variant B): `OPT IN` while the player is
   * out, the neutral `PLAY NOW` treatment once they are in — or when they are barred from joining at
   * all, since `canParticipate` is the segment/jurisdiction check and offering OPT IN to a player
   * the popup will refuse reads as a broken button.
   *
   * Also the gate on {@link optIn}: the promo card's CTA now joins in place rather than opening the
   * popup to do it, so the label and the action are one decision. The other two surfaces (the round
   * nav icon, the card body) still only open the popup — they carry no CTA of their own.
   */
  @computed
  public get showOptInCta(): boolean {
    return this.canOptInFor(toJackpotCoin(this.rootStore.coinTypeStore.coinType));
  }

  /**
   * The widget's pacing decision, and the auto-open it may authorise.
   *
   * Quiet on failure like the games list: no decision leaves the widget rendering as it does today
   * (see {@link widgetAllowsDisplay}) rather than blanking a lobby surface on a core blip. Shares
   * `requestId` with eligibility so a coin switch discards a late response the same way.
   */
  private async fetchWidgetDecision(coinType: JackpotCoinType, requestId: number): Promise<void> {
    try {
      const response = await getRequest<ApiResponse<JackpotWidgetDecision>>(WIDGET_PATH, { coinType });

      if (requestId !== this.requestId[coinType]) {
        return;
      }

      const decision = parseWidgetDecision(response);

      // Always under the coin we asked for, even when the body did not parse: the slot IS the coin, so
      // a null decision still has to SETTLE that coin. Passing null here left it stuck at "not asked"
      // and hid the widget for good.
      this.setWidgetDecision(decision, coinType);

      /*
       * The widget disappearing a moment after the lobby paints is what this pacing looks like when it
       * hides something, and from the outside it is indistinguishable from a bug. `reason` is core's
       * own account of which rule did it — HIDDEN_BY_CONFIG is an operator's off switch (fix it in
       * admin), OUTSIDE_DISPLAY_WINDOW is the feature working, NOT_CONFIGURED can no longer hide.
       */
      if (decision && !decision.show) {
        devWarn('JackpotStore: jackpot widget hidden by core', decision.reason, decision.segmentKey ?? '');
      }

      /*
       * ONLY the coin on screen. This method runs for both coins now, and `maybeAutoOpenPopup` reads
       * the SELECTED coin's decision, not this response's — so letting the inactive coin call it lets
       * a stale `jump: true` open the popup while the selected coin's own fresh answer is still in
       * flight, even when that answer turns out to be `jump: false`.
       */
      if (coinType === toJackpotCoin(this.rootStore.coinTypeStore.coinType)) {
        this.maybeAutoOpenPopup();
      }
    } catch (error) {
      devError('JackpotStore: fetchWidgetDecision error', getErrorMessage(error));

      if (requestId === this.requestId[coinType]) {
        // Null, not a hide: an outage must not be the thing that removes the widget. Filed under the
        // coin it was asked for, so it settles that coin without touching the other's decision.
        this.setWidgetDecision(null, coinType);
      }
    }
  }

  /**
   * Opens the jackpot popup on core's say-so — the widget "jump".
   *
   * Three conditions, and each one is load-bearing:
   *   - core said `jump`. This side never decides to jump on its own.
   *   - the widget is actually on screen (`canShowWidget`). Opening a popup for a surface the player
   *     cannot see would arrive out of nowhere.
   *   - no dialog is already open. Stacking this over a cash offer, a game, or the popup itself would
   *     interrupt something the player chose.
   *
   * Opening it makes game-ui report the impression, which is what moves `last_jump_at` and starts
   * core's cooldown — so the write that paces this is a consequence of the open, not something this
   * method has to do.
   */
  private maybeAutoOpenPopup(): void {
    if (this.hasAutoOpenedPopup || !this.widgetDecisionForSelectedCoin?.jump || !this.canShowWidget) {
      return;
    }

    if (this.rootStore.uiStore.isDialogOpen || this.rootStore.gameStore.currentGame) {
      return;
    }

    this.hasAutoOpenedPopup = true;
    // Nobody tapped — see the `trigger` note in `openJackpotDialog`.
    this.rootStore.gameUiStore.openJackpotDialog({ trigger: 'auto' });
  }

  /**
   * Wins this lobby has already opened the ceremony for, so a player who closes it without finishing
   * is not handed the same popup on every return to the lobby. Session-lived on purpose: the win is
   * still PENDING server-side and the next session — or the in-slot banner — offers it again.
   */
  private autoOpenedWinIds = new Set<string>();

  /**
   * An unclaimed jackpot win opens its ceremony when the player comes back to the lobby.
   *
   * A win is decided by Splash mid-spin and lands on the in-slot banner as `CLAIM`, which the player
   * can simply walk away from — the money then sits PENDING with nothing in the lobby saying so. The
   * lobby has no win surface of its own: the ceremony lives in game-ui, so what this does is open it in
   * WIN-ONLY mode, where that document renders nothing itself and the ceremony plays over the lobby
   * (with no launch context it reveals the win immediately rather than waiting for a `CLAIM` tap that
   * has no banner to come from). The popup is deliberately not part of this.
   *
   * Guards, each load-bearing:
   *   - not in a game. There the in-slot banner owns the win, and a dialog dropped over a running slot
   *     is the thing the `CLAIM`-first flow exists to avoid.
   *   - no dialog already open — same rule as the widget jump: stacking over something the player
   *     chose is worse than waiting for the next return.
   *   - once per win. Re-checked AFTER the request, because the await is long enough for a dialog to
   *     have opened in the meantime, and a win is only marked seen once it has actually been shown.
   */
  public async maybeOpenPendingWin({ immediate = false }: { immediate?: boolean } = {}): Promise<void> {
    /*
     * COALESCED, because four things ask for this and three of them fire on page load.
     *
     * The reaction above re-runs as the LD flag, auth and coin each resolve; `GameIframe` asks on mount;
     * React's StrictMode double-invokes that effect in development. Measured: five `wins?status=PENDING`
     * requests in the first second of a lobby load, all with the same answer, and the route is
     * `no-store` so every one of them is a full round trip to core.
     *
     * TWO guards, and they catch different things. The in-flight promise collapses SIMULTANEOUS callers
     * (StrictMode's double-invoke, two surfaces mounting together) into one request. The floor collapses
     * callers spread over SECONDS, which is what the reaction's re-fires actually are — see
     * {@link PENDING_WIN_MIN_INTERVAL_MS}. Only having the first is why a lobby load still cost four.
     *
     * `immediate` skips the floor, never the in-flight share. It exists for exactly one caller: leaving
     * a game. A player who just won there must not be told about it up to half a minute later because
     * the lobby happened to check on the way in.
     */
    if (this.pendingWinCheck) {
      return this.pendingWinCheck;
    }

    const floor = immediate ? PENDING_WIN_IMMEDIATE_MIN_INTERVAL_MS : PENDING_WIN_MIN_INTERVAL_MS;

    if (Date.now() - this.lastPendingWinCheckAt < floor) {
      return undefined;
    }

    this.pendingWinCheck = this.runPendingWinCheck().finally(() => {
      this.pendingWinCheck = null;
    });

    return this.pendingWinCheck;
  }

  /** In flight, so simultaneous callers share one request instead of issuing their own. */
  private pendingWinCheck: Promise<void> | null = null;

  /** When the last check actually reached the server — see the coalescing note above. */
  private lastPendingWinCheckAt = 0;

  private async runPendingWinCheck(): Promise<void> {
    if (!this.isEnabled || !this.rootStore.authStore.authorized) {
      return;
    }

    // Read at call time rather than observed: this fires on a route change, and the SPA's path is
    // what "in a game" means here (`/games/:id`, the same prefix GameIframe keys its own visibility on).
    if (window.location.pathname.startsWith('/games') || this.rootStore.uiStore.isDialogOpen) {
      return;
    }

    // Stamped here, not in the guards above: a call that returned without asking the server should not
    // suppress the next one.
    this.lastPendingWinCheckAt = Date.now();

    try {
      const response = await getRequest<ApiResponse<{ id?: string }[]>>(WINS_PATH, { status: 'PENDING' });
      const pending = parsePendingWinIds(response).filter((id) => !this.autoOpenedWinIds.has(id));

      if (!pending.length || this.rootStore.uiStore.isDialogOpen) {
        return;
      }

      pending.forEach((id) => this.autoOpenedWinIds.add(id));
      // The WIN, not the popup: `openJackpotWinDialog` opens game-ui with `winOnly`, so the ceremony is
      // the only thing that appears. A player who just won is not asked to read the ladder first.
      this.rootStore.gameUiStore.openJackpotWinDialog();
    } catch (error) {
      // A read. The win stays PENDING and the next lobby entry — or the next game launch — finds it.
      devError('JackpotStore: pending-win check failed', getErrorMessage(error));
    }
  }

  /** A widget opt-in is in flight — the CTA is held rather than allowed to fire twice. */
  @observable
  public optInPending = false;

  @action
  private setOptInPending(pending: boolean) {
    this.optInPending = pending;
  }

  /**
   * OPT IN from the lobby widget's CTA, without opening the popup.
   *
   * The popup used to be the only caller of `POST /api/v1/jackpot/participation` (FE_DESIGN_SPEC
   * §7.4) so that a player saw the levels, the contribution cost and How-It-Works before joining.
   * That is no longer the flow for the promo card: its CTA already reads `OPT IN`, and it now does
   * what it says. The popup remains the surface for everything else — opting OUT, the ladder, the
   * eligible games — and remains reachable from the card body.
   *
   * Gated on {@link showOptInCta}, so it cannot fire for a player who is already in or who
   * `canParticipate: false` would have the server refuse anyway.
   *
   * The CTA does not flip on the local write: `fetchEligibility` re-reads the row afterwards and the
   * server is what the label follows. One extra round trip, and no chance of a lobby claiming a join
   * that core did not record.
   *
   * On failure the popup opens instead. It is the surface with the retry, the opt-out and the copy
   * explaining what went wrong; silently leaving the button as it was would read as a dead tap. Sent
   * as `auto` so it does not report a second click the player never made.
   */
  public async optIn(
    // The lobby's coin for the widget; the win's coin for the winners banner pop-up.
    coinType: JackpotCoinType = toJackpotCoin(this.rootStore.coinTypeStore.coinType),
    source: JackpotOptInSource = 'LOBBY_WIDGET_CTA',
  ): Promise<boolean> {
    const jackpotId = this.eligibilityFor(coinType)?.jackpotId;
    /*
     * Captured BEFORE the POST, because the player can flip coins while it is in flight.
     *
     * On the legacy path only this coin is re-read afterwards: awaiting the whole fan-out tied the
     * button to the coin the player did not touch, and with no request timeout one unresponsive
     * inactive-coin request left a SUCCESSFUL opt-in spinning for good.
     *
     * The merged path cannot have that failure — there is one request, so the CTA waits on exactly
     * one latency rather than the slower of two. "No request timeout" is still true of this client
     * though, so that single request is bounded explicitly; see {@link OPT_IN_REFRESH_TIMEOUT_MS}.
     */
    if (!this.canOptInFor(coinType) || this.optInPending || !jackpotId) {
      return false;
    }

    this.setOptInPending(true);
    // The widget's own event; the winners banner pop-up's events are not specified yet.
    if (source === 'LOBBY_WIDGET_CTA') trackJackpotWidgetOptIn(coinType);

    try {
      await postRequest(PARTICIPATION_PATH, { jackpotId, participate: true, source });
      await (this.lobbyPollEnabled
        ? this.fetchLobbyState(AbortSignal.timeout(OPT_IN_REFRESH_TIMEOUT_MS))
        : this.fetchEligibilityFor(coinType));

      return true;
    } catch (error) {
      devError('JackpotStore: widget opt-in failed', getErrorMessage(error));
      this.rootStore.gameUiStore.openJackpotDialog({ trigger: 'auto', source: 'cta' });

      return false;
    } finally {
      this.setOptInPending(false);
    }
  }

  /**
   * Both coins, every tick. The inactive coin is fetched so that switching to it is instant; that
   * doubles these two endpoints' traffic, which is the price of never showing a blank widget on a
   * toggle. If it ever needs trimming, the inactive coin is the one to poll less often — its figure
   * only has to be close, and the sweep corrects it on the next tick after a switch.
   */
  public async fetchEligibility(): Promise<void> {
    if (this.lobbyPollEnabled) {
      await this.fetchLobbyState();

      return;
    }

    await Promise.all(JACKPOT_COINS.map((coinType) => this.fetchEligibilityFor(coinType)));
  }

  /**
   * ONE request for both coins and both halves — what replaces the four above.
   *
   * The request id is bumped for BOTH coins and checked for both on the way back, which keeps the
   * sign-out guard working unchanged: {@link clearEligibility} bumps both ids first, so a response
   * already on the wire lands on this check instead of on the store. That ordering is the whole
   * defence against the previous player's jackpot reappearing in the next session's lobby.
   */
  private async fetchLobbyState(abortSignal?: AbortSignal): Promise<void> {
    if (!this.isEnabled || !this.rootStore.authStore.authorized) {
      return;
    }

    const requestId: ByCoin<number> = { CC: this.requestId.CC + 1, SC: this.requestId.SC + 1 };

    /*
     * A CALLER WITH ITS OWN DEADLINE NEITHER JOINS THE SHARE NOR IS JOINED BY IT.
     *
     * The share hands back the in-flight promise verbatim, so both directions lose something real:
     *
     *  - joining would DISCARD this caller's `abortSignal`. `optIn` passes one precisely so the CTA
     *    cannot spin for ever, and a tick already in flight would have silently taken that bound away
     *    again — the bug the timeout was added to prevent.
     *  - being joined would hand an unrelated caller a promise that aborts on SOMEONE ELSE'S deadline.
     *    A tick arriving during an opt-in would inherit a 10s abort it never asked for.
     *
     * Opt-in is a tap, not a poll, so the cost of not sharing is one request per press. The request id
     * is still bumped, so this response supersedes the tick's rather than racing it.
     */
    if (abortSignal) {
      this.requestId = requestId;
      this.lastFetchAt = Date.now();

      return this.runLobbyFetch(requestId, abortSignal);
    }

    /*
     * IN FLIGHT, shared. Three things ask for this — the tick, the visibility/leave-game refresh, and
     * the dialog-close handler in `GameUiStore` — and React remounts the effect behind the second one,
     * so two calls land a few milliseconds apart. Without this each issued its own request: measured as
     * two `lobby` round trips on a single lobby load.
     */
    if (this.lobbyFetch) {
      return this.lobbyFetch;
    }

    this.requestId = requestId;
    /*
     * Stamped HERE, not after the response. The staleness floor exists to stop a burst, and a floor
     * that only starts counting once the first request has come back cannot stop one — every caller
     * inside that round trip still reads the previous value.
     */
    this.lastFetchAt = Date.now();

    this.lobbyFetch = this.runLobbyFetch(requestId, abortSignal).finally(() => {
      this.lobbyFetch = null;
    });

    return this.lobbyFetch;
  }

  /** In flight, so simultaneous callers share one request instead of issuing their own. */
  private lobbyFetch: Promise<void> | null = null;

  private async runLobbyFetch(requestId: ByCoin<number>, abortSignal?: AbortSignal): Promise<void> {
    try {
      const response = await getRequest<ApiResponse<JackpotLobbyState>>(LOBBY_PATH, undefined, abortSignal);

      if (!this.isCurrentRequest(requestId)) {
        return;
      }

      this.applyLobbyState(parseLobbyState(response));
    } catch (error) {
      // The endpoint never 404s for "no jackpot" — it answers `canSee: false` per coin — so a throw
      // here is a real outage. Degrade both coins, which reads as canSee false and renders nothing.
      devError('JackpotStore: fetchLobbyState error', getErrorMessage(error));

      if (this.isCurrentRequest(requestId)) {
        this.applyLobbyState(null);
      }
    }
  }

  /** Whether a merged response is still the newest one asked for, on both coins. */
  private isCurrentRequest(requestId: ByCoin<number>): boolean {
    return JACKPOT_COINS.every((coinType) => this.requestId[coinType] === requestId[coinType]);
  }

  private async fetchEligibilityFor(coinType: JackpotCoinType): Promise<void> {
    if (!this.isEnabled || !this.rootStore.authStore.authorized) {
      return;
    }

    const requestId = this.requestId[coinType] + 1;
    this.requestId = { ...this.requestId, [coinType]: requestId };

    /*
     * Started alongside eligibility, not chained behind it. The widget renders only once BOTH have
     * landed (see `widgetAllowsDisplay`), so chaining would delay it by a whole round trip — and
     * rendering before the decision arrives is what made it flash in and disappear.
     */
    void this.fetchWidgetDecision(coinType, requestId);

    try {
      const response = await getRequest<ApiResponse<JackpotEligibility>>(ELIGIBILITY_PATH, { coinType });

      if (requestId === this.requestId[coinType]) {
        this.setEligibility(parseEligibility(response), coinType);
      }
    } catch (error) {
      // The endpoint never 404s for "no jackpot" — it answers `canSee: false` — so a throw here is a
      // real outage. Degrade to no data, which reads as canSee false and renders nothing.
      devError('JackpotStore: fetchEligibility error', getErrorMessage(error));

      if (requestId === this.requestId[coinType]) {
        this.setEligibility(null, coinType);
        // The decision request is independent and settles itself; a failure here is not one there.
      }
    }
  }

  public stopPolling() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }
}
